import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Clock } from "lucide-react";
import api from "../api/client";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import CursorDot from "../components/CursorDot";
import NewsletterCTA from "../components/NewsletterCTA";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/blogs")
      .then((r) => setPosts(r.data))
      .catch(() => setError("Could not load posts."))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <main style={{ background: "#EBEBEB" }}>
      <Helmet>
        <title>Blog — LinkedIn Lab</title>
        <meta
          name="description"
          content="Notes on LinkedIn for B2B founders: content, conversion infrastructure, pipeline, and what actually moves the needle."
        />
        <link rel="canonical" href="https://thelinkedinlab.in/blog" />
        <meta property="og:title" content="Blog — LinkedIn Lab" />
        <meta
          property="og:description"
          content="Notes on LinkedIn for B2B founders: content, conversion infrastructure, pipeline."
        />
        <meta property="og:url" content="https://thelinkedinlab.in/blog" />
        <meta property="og:image" content="https://thelinkedinlab.in/og-image.png" />
      </Helmet>

      <CursorDot />
      <Navbar />

      <section className="pt-[180px] pb-12">
        <div className="container-x">
          <Reveal>
            <div className="max-w-3xl">
              <div className="font-script" style={{ fontSize: 22, color: "var(--agero-orange)" }}>
                (Blog)
              </div>
              <h1
                className="font-display mt-3"
                style={{
                  fontSize: "clamp(48px, 7vw, 110px)",
                  letterSpacing: "-0.05em",
                  lineHeight: 0.95,
                }}
              >
                Notes on LinkedIn
                <br />
                <span className="gradient-orange-text">that actually rank.</span>
              </h1>
              <p className="mt-6 text-neutral-700 text-[18px] leading-relaxed max-w-2xl">
                Field notes on what works on LinkedIn for B2B founders.
                Content systems, conversion infrastructure, and the gap between
                impressions and pipeline.
              </p>
            </div>
          </Reveal>

          <Reveal delay={140} className="block mt-10">
            <NewsletterCTA variant="banner" />
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          {loading && (
            <div className="py-20 text-center text-neutral-500">Loading posts...</div>
          )}
          {error && (
            <div className="py-20 text-center text-neutral-500">{error}</div>
          )}
          {!loading && !error && posts.length === 0 && (
            <div className="py-20 text-center text-neutral-500">
              No posts published yet. Check back soon.
            </div>
          )}

          {featured && (
            <Reveal>
              <Link
                to={`/blog/${featured.slug}`}
                className="block rounded-[32px] overflow-hidden border border-neutral-200/70 bg-white hover:shadow-xl transition-shadow"
              >
                <div className="grid lg:grid-cols-[1.1fr_1fr] gap-0">
                  <div
                    className="relative"
                    style={{
                      minHeight: 360,
                      background: featured.cover_image
                        ? `center/cover no-repeat url('${featured.cover_image}')`
                        : "linear-gradient(135deg, #0A0A0A 0%, #2a0a02 60%, #ff3301 140%)",
                    }}
                  >
                    {!featured.cover_image && (
                      <div className="absolute inset-0 flex items-center justify-center text-white/85 font-display text-4xl px-8 text-center">
                        {featured.title}
                      </div>
                    )}
                  </div>
                  <div className="p-8 md:p-10 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-4">
                        <span
                          className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase"
                          style={{ background: "rgba(255,51,1,0.10)", color: "var(--agero-orange)" }}
                        >
                          Featured
                        </span>
                        {(featured.tags || []).slice(0, 2).map((t) => (
                          <span
                            key={t}
                            className="px-2.5 py-1 rounded-full text-[11px] font-medium"
                            style={{ background: "#f1f1f1", color: "#444" }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <h2
                        className="font-display"
                        style={{
                          fontSize: "clamp(28px, 3vw, 42px)",
                          letterSpacing: "-0.03em",
                          lineHeight: 1.05,
                        }}
                      >
                        {featured.title}
                      </h2>
                      <p className="mt-5 text-neutral-700 leading-relaxed">
                        {featured.excerpt}
                      </p>
                    </div>
                    <div className="mt-8 flex items-center justify-between text-sm text-neutral-500">
                      <div className="flex items-center gap-3">
                        <span>{formatDate(featured.published_at)}</span>
                        <span>&middot;</span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock size={13} />
                          {featured.reading_time} min read
                        </span>
                      </div>
                      <span
                        className="inline-flex items-center gap-1.5 font-medium"
                        style={{ color: "var(--agero-orange)" }}
                      >
                        Read <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          {rest.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {rest.map((p, i) => (
                <Reveal key={p.id} delay={i * 90}>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="block rounded-3xl overflow-hidden bg-white border border-neutral-200/70 hover:shadow-lg transition-shadow h-full"
                  >
                    <div
                      style={{
                        minHeight: 200,
                        background: p.cover_image
                          ? `center/cover no-repeat url('${p.cover_image}')`
                          : "linear-gradient(135deg, #0A0A0A 0%, #2a0a02 60%, #ff3301 140%)",
                      }}
                    />
                    <div className="p-6">
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        {(p.tags || []).slice(0, 2).map((t) => (
                          <span
                            key={t}
                            className="px-2 py-0.5 rounded-full text-[10.5px] font-medium"
                            style={{ background: "#f1f1f1", color: "#444" }}
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <h3
                        className="font-display"
                        style={{
                          fontSize: 22,
                          letterSpacing: "-0.02em",
                          lineHeight: 1.15,
                        }}
                      >
                        {p.title}
                      </h3>
                      <p className="mt-3 text-neutral-600 text-[14.5px] leading-relaxed line-clamp-3">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 flex items-center justify-between text-xs text-neutral-500">
                        <span>{formatDate(p.published_at)}</span>
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} />
                          {p.reading_time} min
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
